import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Task,
  TaskLink,
  TaskComment,
  TasksBoardData,
  IProject,
  ProjectInsights,
} from '@/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ProjectsNewState {
  projects: IProject[];
  selectedProject: IProject | Record<string, never>;
  selectedProjectTasks: Task[];
  selectedTask: Task | Record<string, never>;
  selectedTaskLinks: TaskLink[];
  selectedTaskComments: TaskComment[];
  tasksBoardData: TasksBoardData;
  projectInsights: ProjectInsights;

  updateProjects: (projects: IProject[]) => void;
  updateSelectedProject: (project: IProject | Record<string, never>) => void;
  updateSelectedProjectTasks: (tasks: Task[]) => void;
  updateSelectedTask: (task: Task | Record<string, never>) => void;
  updateSelectedTaskLinks: (links: TaskLink[]) => void;
  updateSelectedTaskComments: (comments: TaskComment[]) => void;
  updateTasksBoardData: (
    dataOrUpdater: TasksBoardData | ((prev: TasksBoardData) => TasksBoardData)
  ) => void;
  updateProjectInsights: (insights: ProjectInsights) => void;
}

const useProjectsStoreNew = create<ProjectsNewState>()(
  devtools(
    (set) => ({
      projects: [],
      selectedProject: {},
      selectedProjectTasks: [],
      selectedTask: {},
      selectedTaskLinks: [],
      selectedTaskComments: [],
      tasksBoardData: {},
      projectInsights: {
        project_delivery_risk: '',
        blockers_summary: '',
        wins_summary: '',
      },

      // =====================================
      updateProjects: (projects) => set({ projects }, false, 'updateProjects'),
      updateSelectedProject: (project) =>
        set({ selectedProject: project }, false, 'updateSelectedProject'),
      updateSelectedProjectTasks: (selectedProjectTasks) =>
        set({ selectedProjectTasks }, false, 'updateSelectedProjectTasks'),
      updateSelectedTask: (task) =>
        set({ selectedTask: task }, false, 'updateSelectedTask'),
      updateSelectedTaskLinks: (selectedTaskLinks) =>
        set({ selectedTaskLinks }, false, 'updateSelectedTaskLinks'),
      updateSelectedTaskComments: (selectedTaskComments) =>
        set({ selectedTaskComments }, false, 'updateSelectedTaskComments'),

      updateTasksBoardData: (tasksBoardDataOrUpdater) =>
        set(
          (state) => ({
            tasksBoardData:
              typeof tasksBoardDataOrUpdater === 'function'
                ? tasksBoardDataOrUpdater(state.tasksBoardData)
                : tasksBoardDataOrUpdater,
          }),
          false,
          'updateTasksBoard'
        ),

      updateProjectInsights: (insights) =>
        set({ projectInsights: insights }, false, 'updateProjectInsights'),
    }),
    { name: 'ProjectsStoreNew' }
  )
);

export default useProjectsStoreNew;
